import { isListNotEmpty, isNotNullOrUndefined, isObjectNotEmpty, isStringEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from "../utils/objectUtils";
import { addValueToChildArray, create, getAll, getById, getByIdQuery, getBySingleQuery, remove, removeValueFromChildArray, update, updateField } from '../utils/dbUtils';
import { collection, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where } from "firebase/firestore";
import { asTimeZone } from "../utils/dateFormatter";
import { db } from "../firebase";
import moment from "moment-timezone";

class ShippingClass {
    static collectionName = 'shippingClasses';

    name = ""
    description = ""
    productIds = []; // products that have this shipping class

    constructor(shippingClass) {
        if (isObjectNotEmpty(shippingClass)) {
            if (isStringNotEmpty(shippingClass.name)) { this.name = shippingClass.name; }
            if (isStringNotEmpty(shippingClass.description)) { this.description = shippingClass.description; }
            if (isListNotEmpty(shippingClass.productIds)) { this.productIds = shippingClass.productIds; }
        }
    }

    toDto = () => {
        return {
            name: this.name,
            description: this.description,
            productIds: this.productIds,
        };
    }

    isValid = () => {
        if (isStringEmpty(this.name)) {
            throw new Error('Shipping class name is required.');
        }

        return true;
    }

    create = async () => {
        if (this.isValid()) {
            return await create(ShippingClass.collectionName, this.toDto());
        }
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(ShippingClass.collectionName, id, fieldName, newValue);
        } else {
            throw new Error("id is required.");
        }
    }

    static update = async (id, updatedShippingClass) => {
        const updatedObj = new ShippingClass(updatedShippingClass);
        if (updatedObj.isValid() && id === updatedShippingClass.id) {
            return await update(ShippingClass.collectionName, id, updatedObj.toDto());
        } else {
            throw new Error('id and id field of the updated shipping class must match.')
        }
    }

    addProductId = async (id, productId) => {
        if (isStringNotEmpty(id) && isStringNotEmpty(productId)) {
            return await addValueToChildArray(ShippingClass.collectionName, id, 'productIds', productId);
        }

        return false;
    }

    removeProductId = async (id, productId) => {
        if (isStringNotEmpty(id) && isStringNotEmpty(productId)) {
            return await removeValueFromChildArray(ShippingClass.collectionName, id, 'productIds', productId);
        }

        return false;
    };

    static delete = async (id) => {
        if (isStringNotEmpty(id)) {
            return await remove(ShippingClass.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getById = async (id) => {
        if (isStringNotEmpty(id)) {
            return await getById(ShippingClass.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getAll = async () => {
        return await getAll(ShippingClass.collectionName);
    }

    static getByField = async (fieldName, fieldValue, comparer = '==') => {
        return await getBySingleQuery(ShippingClass.collectionName, fieldName, fieldValue, comparer);
    }

    static getByIds = async (ids = []) => {
        return await getByIdQuery(ShippingClass.collectionName, ids, 'in');
    }

    static getCount = async (queryParameters) => {
        const { productId, start, end } = queryParameters;

        let queryConstraints = [];

        if (isListNotEmpty(productId)) {
            queryConstraints.push(where('productIds', 'array-contains-any', productId));
        } else if (isStringNotEmptyOrZeroLength(productId)) {
            queryConstraints.push(where('productIds', 'array-contains', productId));
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        let q = query(collection(db, ShippingClass.collectionName), ...queryConstraints);

        const snapShot = await getCountFromServer(q);

        //console.log(snapShot.data());

        return snapShot.data().count;
    }

    static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt') => {
        const { productId, start, end } = queryParameters;

        let queryConstraints = [];

        if (isListNotEmpty(productId)) {
            queryConstraints.push(where('productIds', 'array-contains-any', productId));
        } else if (isStringNotEmptyOrZeroLength(productId)) {
            queryConstraints.push(where('productIds', 'array-contains', productId));
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        let q = null;
        if (isObjectNotEmpty(firstVisible)) {
            q = query(collection(db, ShippingClass.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
        } else if (isObjectNotEmpty(lastVisible)) {
            q = query(collection(db, ShippingClass.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
        } else {
            q = query(collection(db, ShippingClass.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
        }

        let snapShot = await getDocs(q);
        return snapShot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }

    static get = async (queryParameters) => {
        let allRecords = [];
        try {
            let next = null;
            let totalPages = 0;

            let totalCount = await this.getCount(queryParameters);
            totalPages = Math.ceil(totalCount / 10000.0);
            // console.log('totalCount: ' + totalCount);
            // console.log('totalPages: ' + totalPages);
            let pages = [];
            for (let i = 0; i < totalPages; i++) {
                pages.push(i + 1);
            }
            for (const page of pages) {
                // console.log('starting page ' + page);

                let list = await this.getPaginated(queryParameters, 10000, null, next, 'createdAt');

                // console.log(list.length);
                allRecords = [...allRecords, ...list];

                // console.log(allRecords.length);

                // console.log('go to next page');
                next = list[list.length - 1];
            }
        } catch (error) {
            console.log(error);
        }

        return allRecords;
    }
}

export default ShippingClass;